import React from 'react';
import { Pressable, Text } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Unicon from '@/components/General/Unicon';
import useTooltipStore from '@/data/store/useTooltipStore';

/**
 * A lightweight action button with icon and text, styled with a subtle border.
 * Ideal for secondary actions like "Check In", "Resources", "Phone Call", etc.
 * 
 * @param {object} props - The properties for the ActionButton component.
 * @param {string} [props.text=""] - The text for the button.
 * @param {string} [props.iconName=""] - The name of the Unicon icon to display.
 * @param {Function|null} [props.onPress=null] - Callback function when the button is pressed.
 * @param {boolean} [props.disabled=false] - Whether the button is disabled.
 * @param {boolean} [props.primary=false] - Whether to use primary styling (filled background).
 * @param {string} [props.testID=""] - The test ID for the button.
 * @param {string} [props.tooltip=""] - Tooltip text to show on hover/long press.
 * @returns {JSX.Element} - The rendered action button component.
 */
const ActionButton = ({ text = "", iconName = "", iconLibrary = 'ionicon', onPress = undefined, disabled = false, primary = false, testID = "", tooltip = "" }) => {
    // Stable actions only — no re-render on store writes.
    const open = useTooltipStore(s => s.open);
    const close = useTooltipStore(s => s.close);

    return (
        <Pressable
            testID={testID || `ActionButton-${text}`}
            disabled={disabled}
            onPress={onPress}
            onHoverIn={(e) => {
                if (tooltip) {
                    open(e.nativeEvent.pageX, e.nativeEvent.pageY, tooltip);
                }
            }}
            onHoverOut={() => {
                if (tooltip) close();
            }}
            style={styles.button(primary, disabled, !!onPress)}
        >
            {!!iconName && (
                <Unicon name={iconName} library={iconLibrary} size={16} colorVariant={primary ? "onPrimary" : "text"} />
            )}
            {!!text && (
                <Text style={styles.buttonText(primary)}>{text}</Text>
            )}
        </Pressable>
    );
};

const styles = StyleSheet.create(({ colors, fonts }) => ({
    button: (primary, disabled, hasPress) => ({
        flex: 1,
        minWidth: 100,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        paddingVertical: 10,
        paddingHorizontal: 12,
        borderRadius: 8,
        borderWidth: primary ? 0 : 1,
        borderColor: colors.border,
        backgroundColor: primary ? colors.primary : colors.surface,
        opacity: disabled ? 0.5 : 1,
        pointerEvents: hasPress ? 'auto' : 'none',
        _web: {
            transition: 'all 0.15s ease-out',
            cursor: disabled ? 'not-allowed' : 'pointer',
            userSelect: 'none',
            _hover: {
                backgroundColor: primary ? colors.primary : colors.hover,
                opacity: disabled ? 0.5 : 1
            },
            _active: {
                transform: [{ scale: 0.98 }]
            }
        }
    }),
    buttonText: (primary) => ({
        color: primary ? colors.onPrimary : colors.text,
        fontSize: 13,
        fontFamily: fonts.bold
    })
}));

export default React.memo(ActionButton);
